import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MealPlanCard = ({ meal }) => {
  const [expanded, setExpanded] = useState(false);

  const getMealTypeColor = (type) => {
    const colors = {
      breakfast: 'bg-warning/10 text-warning',
      lunch: 'bg-success/10 text-success',
      dinner: 'bg-primary/10 text-primary',
      snack: 'bg-accent/10 text-accent'
    };
    return colors?.[type?.toLowerCase()] || 'bg-muted text-muted-foreground';
  };

  const getDifficultyIcon = (difficulty) => {
    const icons = {
      easy: 'ThumbsUp',
      medium: 'Zap',
      hard: 'Flame'
    };
    return icons?.[difficulty?.toLowerCase()] || 'Info';
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-md overflow-hidden transition-smooth hover:shadow-lg">
      {/* Card Header */}
      <div className="p-4 md:p-5 border-b border-border">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${getMealTypeColor(meal?.mealType)}`}>
                {meal?.mealType || 'Meal'}
              </span>
              {meal?.difficulty && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Icon name={getDifficultyIcon(meal?.difficulty)} size={14} />
                  {meal?.difficulty}
                </span>
              )}
            </div>
            <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-1">
              {meal?.name}
            </h3>
            <p className="text-sm text-muted-foreground">
              {meal?.description}
            </p>
          </div>
        </div>

        {/* Nutrition Summary */}
        <div className="grid grid-cols-4 gap-3 mt-4">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Icon name="Flame" size={16} color="var(--color-warning)" />
            </div>
            <p className="text-lg font-semibold text-foreground">{meal?.nutrition?.calories || 0}</p>
            <p className="text-xs text-muted-foreground">Calories</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Icon name="Beef" size={16} color="var(--color-error)" />
            </div>
            <p className="text-lg font-semibold text-foreground">{meal?.nutrition?.protein || 0}g</p>
            <p className="text-xs text-muted-foreground">Protein</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Icon name="Wheat" size={16} color="var(--color-warning)" />
            </div>
            <p className="text-lg font-semibold text-foreground">{meal?.nutrition?.carbs || 0}g</p>
            <p className="text-xs text-muted-foreground">Carbs</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Icon name="Droplet" size={16} color="var(--color-primary)" />
            </div>
            <p className="text-lg font-semibold text-foreground">{meal?.nutrition?.fats || 0}g</p>
            <p className="text-xs text-muted-foreground">Fats</p>
          </div>
        </div>

        {/* Prep Time */}
        {meal?.prepTime && (
          <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
            <Icon name="Clock" size={16} />
            <span>Prep time: {meal?.prepTime} minutes</span>
          </div>
        )}
      </div>

      {/* Expandable Details */}
      <div className="p-4 md:p-5">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          iconName={expanded ? 'ChevronUp' : 'ChevronDown'}
          iconPosition="right"
          fullWidth
          className="justify-between"
        >
          {expanded ? 'Hide Details' : 'View Ingredients & Instructions'}
        </Button>

        {expanded && (
          <div className="mt-4 space-y-4">
            {/* Ingredients */}
            {meal?.ingredients && meal?.ingredients?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Icon name="ShoppingCart" size={16} color="var(--color-primary)" />
                  Ingredients
                </h4>
                <ul className="space-y-1">
                  {meal?.ingredients?.map((ingredient, index) => (
                    <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{ingredient}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Instructions */}
            {meal?.instructions && (
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Icon name="BookOpen" size={16} color="var(--color-primary)" />
                  Cooking Instructions
                </h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {meal?.instructions}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MealPlanCard;